/**
 * Child Profile Service - Database Version
 * Handles child profiles with database persistence and multi-parent co-ownership
 */

import { API_BASE } from '../config/api';
import AuthService from './authService';

class ChildProfileServiceDB {
  static API_CHILD_PROFILES = `${API_BASE}/api/child-profiles`;

  // Use centralized AuthService.handleResponse for graceful auth error handling

  // =====================================================
  // CREATE & READ
  // =====================================================

  /**
   * Create a new child profile
   * @param {object} childData - Child profile data
   * @returns {object} Created child profile
   */
  static async createChildProfile(childData) {
    try {
      const response = await fetch(ChildProfileServiceDB.API_CHILD_PROFILES, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify(childData)
      });

      const result = await AuthService.handleResponse(response);

      // Emit event for other components
      window.dispatchEvent(new CustomEvent('childProfileCreated', {
        detail: result.childProfile
      }));

      return result;
    } catch (error) {
      console.error('Error creating child profile:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all child profiles for current user
   * @returns {array} Array of child profiles
   */
  static async getChildProfilesForParent() {
    try {
      const response = await fetch(ChildProfileServiceDB.API_CHILD_PROFILES, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.profiles || [];
    } catch (error) {
      console.error('Error getting child profiles:', error);
      return [];
    }
  }

  /**
   * Get a specific child profile by ID
   * @param {string} childId - Child profile ID
   * @returns {object|null} Child profile or null
   */
  static async getChildProfile(childId) {
    try {
      const response = await fetch(`${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.profile || null;
    } catch (error) {
      console.error('Error getting child profile:', error);
      return null;
    }
  }

  // =====================================================
  // UPDATE & DELETE
  // =====================================================

  /**
   * Update a child profile
   * @param {string} childId - Child profile ID
   * @param {object} updates - Fields to update
   * @returns {object} Updated profile
   */
  static async updateChildProfile(childId, updates) {
    try {
      const response = await fetch(`${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify(updates)
      });

      const result = await AuthService.handleResponse(response);

      // Emit event
      window.dispatchEvent(new CustomEvent('childProfileUpdated', {
        detail: result.profile
      }));

      return result;
    } catch (error) {
      console.error('Error updating child profile:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Upload (or replace) a child's avatar photo. Multipart; the auth header is
   * applied but NOT Content-Type — the browser must set the multipart
   * boundary itself.
   */
  static async uploadChildPhoto(childId, file) {
    try {
      const form = new FormData();
      form.append('photo', file);
      const response = await fetch(`${API_BASE}/api/child-photos/${childId}`, {
        method: 'POST',
        headers: AuthService.getAuthHeaders(),
        body: form
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error uploading child photo:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Remove a child's avatar photo. Falls back to the emoji avatar.
   */
  static async deleteChildPhoto(childId) {
    try {
      const response = await fetch(`${API_BASE}/api/child-photos/${childId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error deleting child photo:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete a child profile (primary owner only)
   * @param {string} childId - Child profile ID
   * @returns {object} Result
   */
  static async deleteChildProfile(childId) {
    try {
      const response = await fetch(`${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      const result = await AuthService.handleResponse(response);

      // Emit event
      window.dispatchEvent(new CustomEvent('childProfileDeleted', {
        detail: { childId }
      }));

      return result;
    } catch (error) {
      console.error('Error deleting child profile:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // CO-OWNERSHIP MANAGEMENT
  // =====================================================

  /**
   * Add a co-owner to a child profile
   * @param {string} childId - Child profile ID
   * @param {string} coOwnerId - User ID to add as co-owner
   * @param {string} permissionLevel - 'full_access' or 'view_only'
   * @returns {object} Result
   */
  static async addCoOwner(childId, coOwnerId, permissionLevel = 'full_access') {
    try {
      const response = await fetch(`${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/co-owners`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ coOwnerId, permissionLevel })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error adding co-owner:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Who may be added as a co-owner of this child: exactly who the add
   * accepts (the caller's friends, less anyone blocked with a co-owner and
   * the co-owners already there). Full-access co-owners only; [] otherwise.
   * @param {string} childId - Child profile ID
   * @returns {array} [{ id, name, avatar, profilePicture }]
   */
  static async getCoOwnerCandidates(childId) {
    try {
      const response = await fetch(`${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/co-owner-candidates`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.candidates || [];
    } catch (error) {
      console.error('Error getting co-owner candidates:', error);
      return [];
    }
  }

  /**
   * Change an existing co-owner's permission level (C268). Never adds
   * anyone: a target who is no longer a co-owner gets a 404. Full-access
   * callers only; the primary owner's level never changes.
   * @param {string} childId - Child profile ID
   * @param {string} coOwnerId - The co-owner's user ID
   * @param {'full_access'|'view_only'} permissionLevel
   * @returns {object} Result
   */
  static async changeCoOwnerLevel(childId, coOwnerId, permissionLevel) {
    try {
      const response = await fetch(`${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/co-owners/${coOwnerId}`, {
        method: 'PATCH',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ permission_level: permissionLevel })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error changing co-owner level:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Remove a co-owner from a child profile
   * @param {string} childId - Child profile ID
   * @param {string} coOwnerId - User ID to remove
   * @returns {object} Result
   */
  static async removeCoOwner(childId, coOwnerId) {
    try {
      const response = await fetch(`${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/co-owners/${coOwnerId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error removing co-owner:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // UTILITY FUNCTIONS (kept for compatibility)
  // =====================================================

  /**
   * Calculate age group based on birth date
   * @param {string} birthDate - Birth date (YYYY-MM-DD)
   * @returns {string} Age group
   */
  static calculateAgeGroup(birthDate) {
    const today = new Date();
    const birth = new Date(birthDate);
    const ageInYears = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    const age = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())
      ? ageInYears - 1
      : ageInYears;

    if (age < 3) return 'toddler';
    if (age < 6) return 'preschool';
    if (age < 13) return 'elementary';
    if (age < 18) return 'teen';
    return 'adult';
  }

  // =====================================================
  // CHILD FRIENDS MANAGEMENT
  // =====================================================

  /**
   * Get friends (adults) who can see/invite this child
   * @param {string} childId - Child profile ID
   * @returns {array} Array of adult friends
   */
  static async getChildFriends(childId) {
    try {
      const response = await fetch(`${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/friends`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.friends || [];
    } catch (error) {
      console.error('Error getting child friends:', error);
      return [];
    }
  }

  /**
   * Get parent's friends who are eligible to be added to child's friend list
   * @param {string} childId - Child profile ID
   * @returns {array} Array of eligible friends
   */
  static async getEligibleFriendsForChild(childId) {
    try {
      const response = await fetch(`${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/friends/eligible`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.eligibleFriends || [];
    } catch (error) {
      console.error('Error getting eligible friends:', error);
      return [];
    }
  }

  /**
   * Add an adult friend to child's friend list
   * @param {string} childId - Child profile ID
   * @param {string} adultUserId - Adult user ID to add
   * @param {object} options - Options like canInvite, canSeeDetails, notes
   * @returns {object} Result
   */
  static async addChildFriend(childId, adultUserId, options = {}) {
    try {
      const response = await fetch(`${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/friends`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          adultUserId,
          canInvite: options.canInvite !== false,
          canSeeDetails: options.canSeeDetails !== false,
          notes: options.notes
        })
      });

      const result = await AuthService.handleResponse(response);

      // Emit event
      window.dispatchEvent(new CustomEvent('childFriendAdded', {
        detail: { childId, friend: result.friend }
      }));

      return result;
    } catch (error) {
      console.error('Error adding child friend:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Remove an adult from child's friend list
   * @param {string} childId - Child profile ID
   * @param {string} adultUserId - Adult user ID to remove
   * @returns {object} Result
   */
  static async removeChildFriend(childId, adultUserId) {
    try {
      const response = await fetch(`${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/friends/${adultUserId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      const result = await AuthService.handleResponse(response);

      // Emit event
      window.dispatchEvent(new CustomEvent('childFriendRemoved', {
        detail: { childId, adultUserId }
      }));

      return result;
    } catch (error) {
      console.error('Error removing child friend:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update permissions for a child's friend
   * @param {string} childId - Child profile ID
   * @param {string} adultUserId - Adult user ID
   * @param {object} updates - Permission updates
   * @returns {object} Result
   */
  static async updateChildFriendPermissions(childId, adultUserId, updates) {
    try {
      const response = await fetch(`${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/friends/${adultUserId}`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify(updates)
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error updating child friend permissions:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all children that the current user can invite
   * (for activity/trip invitations)
   * @returns {array} Array of invitable children
   */
  static async getInvitableChildren() {
    try {
      const response = await fetch(`${API_BASE}/api/invitable-children`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      // The whole payload, not just data.children. ActivityInviteModal renders
      // own and friends' children as separate sections, and returning the flat
      // array made `childrenData.ownChildren` undefined at the call site — so
      // its `|| childrenData` fallback swept EVERY child into the own-children
      // bucket and left friendsChildren permanently empty.
      return {
        ownChildren: data.ownChildren || [],
        friendsChildren: data.friendsChildren || [],
        children: data.children || []
      };
    } catch (error) {
      console.error('Error getting invitable children:', error);
      return { ownChildren: [], friendsChildren: [], children: [] };
    }
  }

  // ===== Child activity history =====

  /**
   * List a child's past activities (newest first). Includes activities the
   * child did without the requesting parent (logged by a co-owner, or via a
   * cross-account invite). Returns [] on error/empty.
   */
  static async getChildActivityHistory(childId) {
    try {
      const response = await fetch(
        `${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/activity-history`,
        { headers: AuthService.getJsonHeaders() }
      );
      const data = await AuthService.handleResponse(response);
      return data.activities || [];
    } catch (error) {
      console.error('Error getting child activity history:', error);
      return [];
    }
  }

  /**
   * Log a standalone activity for a child (no parent activities row) — e.g. a
   * birthday party or a grandparent outing. Requires full_access. `activity`
   * uses camelCase keys (placeId, rating, engagementLevel, ...).
   */
  static async logChildActivity(childId, activity) {
    try {
      const response = await fetch(
        `${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/activity-history`,
        {
          method: 'POST',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify(activity)
        }
      );
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error logging child activity:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Record the child's reaction for an activity they were tagged at (Path 3 —
   * cross-account invite). Upserts a history row tied to the existing activity.
   * Requires full_access.
   */
  static async recordChildReactionForActivity(childId, activityId, { engagementLevel, notes } = {}) {
    try {
      const response = await fetch(
        `${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/activity-history/from-activity/${activityId}`,
        {
          method: 'POST',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify({ engagementLevel, notes })
        }
      );
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error recording child reaction:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Edit rating/engagement on one of a child's activity-history rows. Requires
   * full_access. `updates` uses camelCase keys.
   */
  static async updateChildActivity(childId, activityHistoryId, updates) {
    try {
      const response = await fetch(
        `${ChildProfileServiceDB.API_CHILD_PROFILES}/${childId}/activity-history/${activityHistoryId}`,
        {
          method: 'PATCH',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify(updates)
        }
      );
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error updating child activity:', error);
      return { success: false, error: error.message };
    }
  }
}

export default ChildProfileServiceDB;
